import React, { useMemo, useState } from 'react';
import { Spin, Pagination } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import {
  SyncOutlined, ReloadOutlined, WarningOutlined,
  CheckCircleOutlined, ClockCircleOutlined,
} from '@ant-design/icons';
import { useGetSyncStatusQuery } from '../../store/api';

dayjs.extend(relativeTime);
dayjs.locale('es');

const PAGE_SIZE = 10;

const STATUS = {
  ok: { label: 'OK', className: 'ok' },
  running: { label: 'EN CURSO', className: 'running' },
  error: { label: 'FALLANDO', className: 'bad' },
  stale: { label: 'CORTADO', className: 'bad' },
  unknown: { label: 'SIN DATO', className: 'unknown' },
};

const STATUS_PRIORITY = {
  error: 0,
  stale: 1,
  running: 2,
  unknown: 3,
  ok: 4,
};

const formatTime = (value) => (value ? dayjs(value).format('DD/MM HH:mm') : '—');

const formatBytes = (bytes) => {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const statusMeta = (status) => STATUS[status] || STATUS.unknown;

const StatusBadge = ({ status }) => {
  const meta = statusMeta(status);
  return <span className={`yzen-sync-badge ${meta.className}`}>{meta.label}</span>;
};

const TypeBadge = ({ kind }) => (
  <span className={`yzen-sync-type ${kind}`}>{kind === 'sync' ? 'SYNC' : 'BACKUP'}</span>
);

const SyncDetail = ({ item }) => {
  if (item.error) {
    return <span className="yzen-sync-detail bad" title={item.error}>{item.error}</span>;
  }
  if (item.status === 'running') {
    return <span className="yzen-sync-detail">Inició {formatTime(item.last_start)}</span>;
  }
  if (item.status === 'stale') {
    return (
      <span className="yzen-sync-detail warn">
        Último registro {formatTime(item.last_evidence)}
      </span>
    );
  }
  if (item.status === 'unknown') {
    return (
      <span className="yzen-sync-detail">
        Sin hora de éxito{item.last_check ? ` · chequeo ${formatTime(item.last_check)}` : ''}
      </span>
    );
  }
  if (item.files_total != null) {
    return (
      <span className="yzen-sync-detail">
        {item.files_total.toLocaleString('es-CO')} archivos
        {item.bytes_transferred ? ` · ${formatBytes(item.bytes_transferred)}` : ''}
      </span>
    );
  }
  return <span className="yzen-sync-detail">—</span>;
};

const BackupDetail = ({ item }) => {
  if (item.error) {
    return <span className="yzen-sync-detail bad" title={item.error}>{item.error}</span>;
  }
  if (item.status === 'stale') {
    return <span className="yzen-sync-detail warn">Sin registro reciente</span>;
  }
  if (item.status === 'unknown') {
    return (
      <span className="yzen-sync-detail">
        Sin hora de éxito{item.last_check ? ` · chequeo ${formatTime(item.last_check)}` : ''}
      </span>
    );
  }
  return <span className="yzen-sync-detail">Backup completado</span>;
};

const SyncStatusPanel = () => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, isError, error, refetch } = useGetSyncStatusQuery(undefined, {
    pollingInterval: 30000,
  });

  const syncs = data?.syncs || [];
  const backups = data?.backups || [];
  const summary = data?.summary || {};
  const issues = Number(summary.syncs_issues || 0) + Number(summary.backups_issues || 0);
  const running = Number(summary.syncs_running || 0);

  const rows = useMemo(() => {
    const syncRows = (data?.syncs || []).map((item) => ({
      key: `sync-${item.id}`,
      kind: 'sync',
      item,
      title: item.name,
      subtitle: item.origin,
      route: `${item.origin} → ${item.destination}`,
      schedule: item.schedule,
      status: item.status,
    }));
    const backupRows = (data?.backups || []).map((item) => ({
      key: `backup-${item.id}`,
      kind: 'backup',
      item,
      title: item.name,
      subtitle: item.server,
      route: `${item.server} (${item.server_ip})`,
      schedule: item.schedule,
      status: item.status,
    }));

    return [...syncRows, ...backupRows].sort((a, b) => {
      const priorityA = STATUS_PRIORITY[a.status] ?? 9;
      const priorityB = STATUS_PRIORITY[b.status] ?? 9;
      if (priorityA !== priorityB) return priorityA - priorityB;
      return a.title.localeCompare(b.title, 'es');
    });
  }, [data]);

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  return (
    <section className="yzen-sync-panel">
      <div className="yzen-sync-header">
        <div>
          <h2 className="yzen-sync-title">
            <SyncOutlined spin={isFetching} />
            Sincronizaciones y backups
            {isFetching && <span className="yzen-sync-live">actualizando</span>}
          </h2>
          <div className="yzen-sync-caption">
            Inventario de crons + `sync_reports` · actualizado {formatTime(data?.generated_at)}
          </div>
        </div>
        <div className="yzen-sync-summary">
          <span className={`yzen-sync-chip ${issues ? 'bad' : 'ok'}`}>
            {issues ? <WarningOutlined /> : <CheckCircleOutlined />}
            {issues ? `${issues} con problemas` : 'Sin problemas'}
          </span>
          <span className="yzen-sync-chip running">
            <ClockCircleOutlined /> {running} en curso
          </span>
          <span className="yzen-sync-chip">
            {syncs.length} syncs · {backups.length} backups
          </span>
          <span className="yzen-sync-chip">
            {Number(summary.backups_ok || 0)}/{Number(summary.backups_total || 0)} backups OK
          </span>
          <button type="button" className="yzen-sync-refresh" onClick={refetch} disabled={isFetching}>
            <ReloadOutlined spin={isFetching} /> Actualizar
          </button>
        </div>
      </div>

      {isError && (
        <div className="yzen-sync-error">
          No se pudo cargar el estado: {error?.data?.error || error?.message || 'error de conexión'}
        </div>
      )}

      {isLoading ? (
        <div className="yzen-sync-loading"><Spin /> Cargando estado de syncs...</div>
      ) : (
        <div className="yzen-sync-block">
          <div className="yzen-sync-block-head">
            <h3>Todos los procesos</h3>
            <span>{rows.length} registros · página {currentPage} de {totalPages}</span>
          </div>
          <div className="yzen-sync-table-wrap yzen-sync-table-wrap-all">
            <table className="yzen-sync-table">
              <thead>
                <tr>
                  <th>Estado</th>
                  <th>Tipo</th>
                  <th>Nombre</th>
                  <th>Servidor / ruta</th>
                  <th>Frecuencia</th>
                  <th>Último éxito</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((row) => (
                  <tr key={row.key}>
                    <td><StatusBadge status={row.status} /></td>
                    <td><TypeBadge kind={row.kind} /></td>
                    <td>
                      <div className="yzen-sync-name">{row.title}</div>
                      <div className="yzen-sync-sub">{row.subtitle}</div>
                    </td>
                    <td className="yzen-sync-route">{row.route}</td>
                    <td>{row.schedule}</td>
                    <td>
                      <div>{formatTime(row.item.last_success)}</div>
                      {row.item.last_success && (
                        <div className="yzen-sync-ago">{dayjs(row.item.last_success).fromNow()}</div>
                      )}
                    </td>
                    <td>
                      {row.kind === 'sync'
                        ? <SyncDetail item={row.item} />
                        : <BackupDetail item={row.item} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="yzen-sync-pagination">
            <Pagination
              current={currentPage}
              pageSize={PAGE_SIZE}
              total={rows.length}
              onChange={setPage}
              showSizeChanger={false}
              showTotal={(total, range) => `${range[0]}–${range[1]} de ${total}`}
              size="small"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default SyncStatusPanel;
